// FROM: https://github.com/modusign/modusign-client/blob/35ac05fa1f9337ff1aeacd765766a3b36653b583/app/src/components/CheckboxField/ConfigurableCheckboxField/index.tsx

import { Box } from '@modusign/design-system';
import cx from 'classnames';
import { MouseEvent, useCallback, useState } from 'react';

import { CheckboxFieldCheck } from '@app/components/CheckboxField';
import {
  FieldBase,
  FieldBaseHandlers,
} from '@app/features/apply-react-signing/components/FieldRenderer/components/FieldBase';
import { createAbsoluteSize } from '@app/features/apply-react-signing/components/FieldRenderer/libs/createAbsoluteSize';
import {
  AbsoluteNumber,
  AbsolutePoint,
  AbsoluteSize,
} from '@app/features/apply-react-signing/components/FieldRenderer/types';
import createFieldClassNameByParticipantOrder from '@app/libs/participantFields/createFieldClassNameByParticipantOrder';

import './ConfigurableCheckboxField.scss';

const DEFAULT_CHECKBOX_SIZE = 20;

export interface ConfigurableCheckboxFieldProps extends FieldBaseHandlers {
  maxConstraint: AbsolutePoint;
  size: AbsoluteNumber;
  viewport: AbsoluteSize;
  point: AbsolutePoint;
  signerOrder: number;
  readOnly?: boolean;
  restrictModification?: boolean;
  index: number;

  // EVENTS
  onMouseEnter: (e: MouseEvent) => void;
  onMouseLeave: (e: MouseEvent) => void;
}

function ConfigurableCheckboxField({
  readOnly,
  restrictModification,
  maxConstraint,
  viewport,
  onMouseEnter,
  onMouseLeave,
  index,
  point,
  signerOrder,
  size,
  onPointChangeStop,
  onPointChangeStart,
  onPointChange,
  onSizeChangeStart,
  onSizeChange,
  onSizeChangeStop,
}: ConfigurableCheckboxFieldProps) {
  const [isResizing, setResizing] = useState(false);
  const [showGuideline, setShowGuideline] = useState(false);

  const handlePointChangeStart = useCallback(
    (point, e) => {
      onPointChangeStart?.(point, e);
      if (!restrictModification) {
        setShowGuideline(true);
      }
      return !restrictModification;
    },
    [onPointChangeStart, restrictModification]
  );

  const handlePointChangeEnd = useCallback(
    (point, e) => {
      onPointChangeStop?.(point, e);
      setShowGuideline(false);
    },
    [onPointChangeStop]
  );

  const handleSizeChangeStart = useCallback(
    (size, e) => {
      onSizeChangeStart?.(size, e);
      setShowGuideline(true);
      setResizing(true);
    },
    [onSizeChangeStart]
  );

  const handleSizeChangeStop = useCallback(
    (size, e) => {
      onSizeChangeStop?.(size, e);
      setShowGuideline(false);
      setResizing(false);
    },
    [onSizeChangeStop]
  );

  const configurableCheckboxFieldClassByOrder = createFieldClassNameByParticipantOrder(
    'ConfigurableCheckboxField',
    signerOrder
  );

  const indexScale = Math.min(size / DEFAULT_CHECKBOX_SIZE, 1);

  return (
    <FieldBase
      bounds={{
        // Draggable의 bound를 parent로 지정하면 jsDOM에서 부모 노드에 제한이 걸리지 않는 이슈가 있습니다.
        // 따라서 값으로 지정합니다.
        left: 0,
        top: 0,
        right: viewport.width - size,
        bottom: viewport.height - size,
      }}
      defaultClassName={cx('ConfigurableCheckboxField', {
        'ConfigurableCheckboxField--resizing': isResizing,
        [configurableCheckboxFieldClassByOrder]: true,
        'ConfigurableCheckboxField__disable-resize': readOnly || restrictModification,
      })}
      dragCancelSelectors={['.react-resizable-handle']}
      fieldGuideline={{
        guideLineOffset: {
          left: 2,
          top: 2,
          right: 1,
          bottom: 1,
        },
        showGuideLine: showGuideline,
        viewport: createAbsoluteSize(viewport),
      }}
      lockAspectRatio
      maxConstraints={[maxConstraint.x, maxConstraint.y]}
      minConstraints={[1, 1]}
      point={point}
      readOnly={readOnly}
      restrictModification={restrictModification}
      size={createAbsoluteSize({ width: size, height: size })}
      onPointChange={onPointChange}
      onPointChangeStart={handlePointChangeStart}
      onPointChangeStop={handlePointChangeEnd}
      onSizeChange={onSizeChange}
      onSizeChangeStart={handleSizeChangeStart}
      onSizeChangeStop={handleSizeChangeStop}
    >
      <Box height="100%" width="100%" onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
        <>
          {indexScale > 0.3 && (
            <Box
              backgroundColor="rgba(0, 0, 0, 0.6)"
              borderRadius="2px 0 0 0"
              color="white"
              fontFamily="Noto Sans CJK KR"
              fontSize="10px"
              fontStyle="normal"
              fontWeight={400}
              padding="2px 4px 3px"
              position="absolute"
              style={{
                transform: `scale(${indexScale}) translate(-1px, -1px)`,
              }}
              sx={{
                transformOrigin: '0 0',
                pointerEvents: 'none',
                userSelect: 'none',
              }}
            >
              {index}
            </Box>
          )}
          <Box height="100%" position="absolute" width="100%">
            <CheckboxFieldCheck opacity={0.3} />
          </Box>
        </>
      </Box>
    </FieldBase>
  );
}

export default ConfigurableCheckboxField;
